import { useLayoutEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import type { CheckoutFormData } from "@/pages/CheckoutPage";

const CURRENT_YEAR = new Date().getFullYear();
const CURRENT_MONTH = new Date().getMonth() + 1; // 1-based
const EXPIRY_YEARS = Array.from({ length: 11 }, (_, i) =>
  String(CURRENT_YEAR + i),
);

const MONTHS = Array.from({ length: 12 }, (_, i) =>
  String(i + 1).padStart(2, "0"),
);

function isAmex(cardNumber: string): boolean {
  const digits = cardNumber.replace(/\D/g, "");
  return digits.startsWith("34") || digits.startsWith("37");
}

/** Index just after the nth digit of a formatted string — where the caret
 *  belongs after an edit that left n digits to its left. */
function caretAfterNthDigit(formatted: string, n: number): number {
  if (n <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (/\d/.test(formatted[i]!)) {
      seen += 1;
      if (seen === n) return i + 1;
    }
  }
  return formatted.length;
}

function formatCardNumber(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (isAmex(digits)) {
    const capped = digits.slice(0, 15);
    if (capped.length <= 4) return capped;
    if (capped.length <= 10) return `${capped.slice(0, 4)}-${capped.slice(4)}`;
    return `${capped.slice(0, 4)}-${capped.slice(4, 10)}-${capped.slice(10)}`;
  }
  const capped = digits.slice(0, 16);
  const groups: string[] = [];
  for (let i = 0; i < capped.length; i += 4) {
    groups.push(capped.slice(i, i + 4));
  }
  return groups.join("-");
}

export function PaymentFields(): React.ReactElement {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<CheckoutFormData>();

  const paymentMethod = watch("paymentMethod");
  const cardNumberValue = watch("card.cardNumber") ?? "";

  // Caret preservation for the re-formatting card field. React rewrites the
  // controlled value with re-grouped dashes on every keystroke, and the DOM
  // resets the caret to the END on each rewrite — so correcting a typo
  // mid-number made digits "dance" (Chuck, production, 2026-07-23). onChange
  // records where the caret belongs (in digits); this effect restores it
  // right after the re-render that applied the formatted value.
  const cardNumberInputRef = useRef<HTMLInputElement | null>(null);
  const pendingCaretRef = useRef<number | null>(null);
  useLayoutEffect(() => {
    const caret = pendingCaretRef.current;
    if (caret !== null && cardNumberInputRef.current) {
      cardNumberInputRef.current.setSelectionRange(caret, caret);
      pendingCaretRef.current = null;
    }
  }, [cardNumberValue]);
  const expireYearValue = watch("card.expireYear") ?? "";
  const cardIsAmex = isAmex(cardNumberValue);
  const selectedYearIsCurrentYear = expireYearValue === String(CURRENT_YEAR);

  const cardErrors = errors.card;

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">Payment Method</h3>

      {/* ACH is deferred (2026-07-22) — card is the only method, so the radio
          group is gone. decisions/2026-07-22-defer-ach-card-only-rebuild.md */}
      <div className="flex gap-4">
        <label className="flex cursor-pointer items-center gap-2">
          <input
            type="radio"
            value="credit_card"
            defaultChecked
            {...register("paymentMethod")}
            className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-gray-700">
            Credit Card
          </span>
        </label>
      </div>

      {paymentMethod === "credit_card" && (
        <div className="space-y-3 rounded-md border border-gray-200 bg-gray-50 p-4">
          <div>
            <label
              htmlFor="card.cardNumber"
              className="block text-sm font-medium text-gray-700"
            >
              Card Number
            </label>
            <input
              id="card.cardNumber"
              type="text"
              inputMode="numeric"
              autoComplete="cc-number"
              maxLength={cardIsAmex ? 17 : 19}
              value={cardNumberValue}
              ref={cardNumberInputRef}
              onChange={(e) => {
                const rawCaret = e.target.selectionStart ?? e.target.value.length;
                const digitsBeforeCaret = e.target.value
                  .slice(0, rawCaret)
                  .replace(/\D/g, "").length;
                const formatted = formatCardNumber(e.target.value);
                pendingCaretRef.current = caretAfterNthDigit(formatted, digitsBeforeCaret);
                setValue("card.cardNumber", formatted, {
                  shouldValidate: true,
                });
              }}
              className={`mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                cardErrors?.cardNumber
                  ? "border-red-500 focus:ring-red-500"
                  : "border-gray-300"
              }`}
            />
            {cardErrors?.cardNumber?.message && (
              <p className="mt-1 text-xs text-red-600">
                {cardErrors.cardNumber.message}
              </p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label
                htmlFor="card.expireMonth"
                className="block text-sm font-medium text-gray-700"
              >
                Month
              </label>
              <select
                id="card.expireMonth"
                autoComplete="cc-exp-month"
                {...register("card.expireMonth")}
                onChange={(e) => {
                  setValue("card.expireMonth", e.target.value, {
                    shouldValidate: true,
                  });
                }}
                className={`mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  cardErrors?.expireMonth
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-300"
                }`}
              >
                <option value="">--</option>
                {MONTHS.map((m) => {
                  const isPast =
                    selectedYearIsCurrentYear && Number(m) < CURRENT_MONTH;
                  return (
                    <option key={m} value={m} disabled={isPast}>
                      {m}
                    </option>
                  );
                })}
              </select>
              {cardErrors?.expireMonth?.message && (
                <p className="mt-1 text-xs text-red-600">
                  {cardErrors.expireMonth.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="card.expireYear"
                className="block text-sm font-medium text-gray-700"
              >
                Year
              </label>
              <select
                id="card.expireYear"
                autoComplete="cc-exp-year"
                {...register("card.expireYear")}
                onChange={(e) => {
                  const newYear = e.target.value;
                  setValue("card.expireYear", newYear, {
                    shouldValidate: true,
                  });
                  // Clear month if it's now a past month
                  const currentMonth = watch("card.expireMonth") ?? "";
                  if (
                    newYear === String(CURRENT_YEAR) &&
                    currentMonth &&
                    Number(currentMonth) < CURRENT_MONTH
                  ) {
                    setValue("card.expireMonth", "", { shouldValidate: true });
                  }
                }}
                className={`mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  cardErrors?.expireYear
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-300"
                }`}
              >
                <option value="">--</option>
                {EXPIRY_YEARS.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
              {cardErrors?.expireYear?.message && (
                <p className="mt-1 text-xs text-red-600">
                  {cardErrors.expireYear.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="card.cvv"
                className="block text-sm font-medium text-gray-700"
              >
                CVV
              </label>
              <input
                id="card.cvv"
                type="text"
                inputMode="numeric"
                autoComplete="cc-csc"
                maxLength={cardIsAmex ? 4 : 3}
                placeholder={cardIsAmex ? "4 digits" : "3 digits"}
                {...register("card.cvv")}
                className={`mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  cardErrors?.cvv
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-300"
                }`}
              />
              {cardErrors?.cvv?.message && (
                <p className="mt-1 text-xs text-red-600">
                  {cardErrors.cvv.message}
                </p>
              )}
            </div>
          </div>

          <div>
            <label
              htmlFor="card.nameOnCard"
              className="block text-sm font-medium text-gray-700"
            >
              Name on Card{" "}
              <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <input
              id="card.nameOnCard"
              type="text"
              autoComplete="cc-name"
              {...register("card.nameOnCard")}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      )}

    </div>
  );
}
